import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { formatNumber, t } from 'ember-intl';
import importAsset from 'lumen/helpers/import-asset';
import lottieAnimation from 'lumen/modifiers/lottie-animation';
import {
  PRICE_PUSH_MILESTONE_SUBTITLE_KEYS,
  type PricePushMilestoneSubtitleKey,
} from 'lumen/utils/roi-trophy/constants';

import GeneralRoiTrophiesContentTrophyModalLayout from './trophy-modal-layout';

interface GeneralRoiTrophiesContentPricePushMilestoneSignature {
  Args: {
    milestone: number;
  };
}

export default class GeneralRoiTrophiesContentPricePushMilestone extends Component<GeneralRoiTrophiesContentPricePushMilestoneSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare router: Services['router'];

  get autopilotUrl(): string {
    return this.router.urlFor('hotel.settings.autopilot');
  }

  get isAutopilotEnabled(): boolean {
    return (
      this.hotelAndMarketService.hotel?.paAutopilotSettings?.enabled ?? false
    );
  }

  get milestoneTier(): 10 | 100 | 500 | 1000 {
    if (this.args.milestone >= 1000) {
      return 1000;
    }
    if (this.args.milestone >= 500) {
      return 500;
    }
    if (this.args.milestone >= 100) {
      return 100;
    }
    return 10;
  }

  get subtitleKey(): PricePushMilestoneSubtitleKey {
    return PRICE_PUSH_MILESTONE_SUBTITLE_KEYS[
      `roi_trophies.price_push_milestone.subtitle_${this.milestoneTier}`
    ];
  }

  <template>
    <GeneralRoiTrophiesContentTrophyModalLayout>
      <:content>
        <div
          class="flex flex-col items-center gap-300"
          data-test-id="content-price-push-milestone"
        >
          <div class="heading-1">
            {{t
              "roi_trophies.price_push_milestone.headline"
              milestone=(formatNumber @milestone)
            }}
          </div>
          <p
            class="text-300--regular text-neutral-subdued"
            data-test-id="price-push-milestone-subtitle"
          >
            {{t this.subtitleKey milestone=(formatNumber @milestone)}}
            {{#unless this.isAutopilotEnabled}}
              <span data-test-id="price-push-milestone-autopilot-cta">{{t
                  "roi_trophies.price_push_milestone.subtitle_autopilot_cta"
                  class="prism-link"
                  htmlSafe=true
                  url=this.autopilotUrl
                }}</span>
            {{/unless}}
          </p>
        </div>
      </:content>
      <:animation>
        <div
          {{! loop=false: play the animation only once }}
          {{lottieAnimation
            (importAsset "animations/roi-trophies/price-update-pushed.json")
            false
          }}
        />
      </:animation>
    </GeneralRoiTrophiesContentTrophyModalLayout>
  </template>
}
